import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BulkSubmittalNotes } from 'src/app/accountmanagers/submittals/add-notes-modal/model/bulk-submittal-notes.model';
import { JobForSubmittal } from 'src/app/accountmanagers/submittals/model/job-for-submittal.model';
import {
    SubmittalJobDeactivate,
    SubmittalJobDeactivateNewStatus
} from 'src/app/accountmanagers/submittals/model/submittal-job-deactivate.model';
import { SubmittalJobRemove } from 'src/app/accountmanagers/submittals/model/submittal-job-remove.model';
import { SubmittalRemove } from 'src/app/accountmanagers/submittals/model/submittal-remove.model';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { combine } from 'src/app/shared/utilities/route';
import { SubmittalRemovalReason } from './model/submittal-removal-reasons.model';
import { SubmittalVendorInfo } from 'src/app/accountmanagers/submittals/model/am-submittals.model';
import { AmSubmittalsBulkRemove } from 'src/app/accountmanagers/submittals/model/am-submittals-bulk-remove.model';

@Injectable({
    providedIn: 'root'
})
export class AmSubmittalsService implements OnDestroy {
    private readonly _coreApiPath: string;

    private readonly _submittalChanged: EventEmitter<{ submittalId: number }> = new EventEmitter<{
        submittalId: number;
    }>();

    get submittalChanged(): Observable<{ submittalId: number }> {
        return this._submittalChanged.asObservable();
    }

    constructor(
        private readonly _httpClient: HttpClient,
        @Inject(APP_CONFIG) private readonly _settings: Settings
    ) {
        this._coreApiPath = combine(this._settings.coreAPIUrl, 'AyaNova');
    }

    ngOnDestroy(): void {
        this._submittalChanged.complete();
    }

    getJobsForSubmittals(submittalIds: number[]): Observable<JobForSubmittal[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/jobs`;
        return this._httpClient.post<JobForSubmittal[]>(url, submittalIds);
    }

    sendEmailsToJobsRecruiters(
        jobIds: number[],
        apnSubject: string,
        apnBody: string,
        ayaSubject: string,
        ayaBody: string,
        sendTo: 'All' | 'Placed' | 'Submitted'
    ): Observable<any> {
        const payload = { jobIds, apnSubject, apnBody, ayaSubject, ayaBody, sendTo };
        const url = `${this._coreApiPath}/accountmanager/submittals/sendemails`;
        return this._httpClient.post<void>(url, payload);
    }

    triggerSubmittalChanged(submittalId: number) {
        this._submittalChanged.next({ submittalId: submittalId });
    }

    addJobToSubmittal(job: JobForSubmittal): Observable<void> {
        const url = `${this._coreApiPath}/accountmanager/submittals/job`;
        return this._httpClient.post<void>(url, job).pipe(tap(() => this.triggerSubmittalChanged(job.submittalId)));
    }

    removeSubmittals(submittals: SubmittalRemove): Observable<any> {
        const url = `${this._coreApiPath}/accountmanager/submittals`;

        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: submittals
        };

        return this._httpClient.delete<any>(url, options);
    }

    removeSubmittalJobs(submittalJobs: SubmittalJobRemove): Observable<any> {
        const url = `${this._coreApiPath}/accountmanager/submittals/jobs`;

        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            }),
            body: submittalJobs
        };

        return this._httpClient.delete<any>(url, options);
    }

    bulkRemoveSubmittalJobs(submittalJobs: AmSubmittalsBulkRemove): Observable<void> {
        const url = `${this._coreApiPath}/accountmanager/submittals/bulk-remove`;

        return this._httpClient.post<void>(url, submittalJobs);
    }

    deactivateSubmittalJobs(submittalJobs: SubmittalJobDeactivate): Observable<boolean> {
        const url = `${this._coreApiPath}/accountmanager/job-submittals/deactivate`;
        return this._httpClient.put<boolean>(url, submittalJobs);
    }

    deactivateSubmittalJobsNewStatus(submittalJobs: SubmittalJobDeactivateNewStatus): Observable<boolean> {
        const url = `${this._coreApiPath}/accountmanager/job-submittals/deactivate-newstatus`;
        return this._httpClient.put<boolean>(url, submittalJobs);
    }

    addNotes(model: BulkSubmittalNotes) {
        const url = `${this._coreApiPath}/accountmanager/submittals/notes`;
        return this._httpClient.post<void>(url, model);
    }

    getOfferRecruiters(): Observable<number[]> {
        const url = `${this._coreApiPath}/accountmanager/submittals/offer-permissions`;
        return this._httpClient.get<number[]>(url);
    }

    getSubmittalRemovalReasons(parentRemovalReasonId: number | null = null): Observable<SubmittalRemovalReason[]> {
        let url = `${this._coreApiPath}/accountmanager/submittals/removal-reasons`;

        if (parentRemovalReasonId) {
            url = `${url}?parentRemovalReasonId=${parentRemovalReasonId}`;
        }

        return this._httpClient.get<SubmittalRemovalReason[]>(url);
    }

    getSubmittalVendorInfo(userId: number): Observable<SubmittalVendorInfo[]> {
        const url = `${this._coreApiPath}/accountmanager/${userId}/submittals/vendor-info`;
        return this._httpClient.get<SubmittalVendorInfo[]>(url);
    }
}
